import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Heart,
  ImagePlus,
  Loader2,
  Save,
  X,
  User,
  GraduationCap,
  Users as FamilyIcon,
  Sparkles,
  MapPin,
  Phone,
  Camera,
  ShieldCheck,
} from "lucide-react";
import useAuthAccess, { invalidateAuthCache, primeAuthCache } from "../hooks/useAuthAccess";
import { doc, serverTimestamp, setDoc } from "firebase/firestore";
import { deleteObject, getDownloadURL, ref, uploadBytes } from "firebase/storage";
import Navbar from "../Landing/Navbar";
import { db, storage, auth } from "../firebase";
import {
  indiaAddressOptions,
  indiaStates,
  residenceTypes,
} from "../data/addressOptions";
import countriesList from "../data/countries";
import { findProfileForUser, getProfileImages } from "../utils/profileLookup";
import { isVerifiedProfile } from "../utils/verification";
import Combobox from "../Components/Combobox";
import { compressImage } from "../utils/imageCompressor";

/* =====================================================================
   Steps + form schema
===================================================================== */
const steps = [
  { id: "personal", title: "Personal", Icon: User, hint: "Tell us about you" },
  { id: "career", title: "Education", Icon: GraduationCap, hint: "Education & career details" },
  { id: "family", title: "Family", Icon: FamilyIcon, hint: "Family details" },
  { id: "interests", title: "Interests", Icon: Heart, hint: "Hobbies & lifestyle" },
  { id: "address", title: "Address", Icon: MapPin, hint: "Where you live" },
  { id: "preferences", title: "Partner Preferences", Icon: Sparkles, hint: "What you're looking for" },
  { id: "contact", title: "Contact", Icon: Phone, hint: "Contact & photos" },
];

const initialForm = {
  // Personal
  profileType: "Myself", fullName: "", gender: "", maritalStatus: "", dateOfBirth: "", timeOfBirth: "",
  height: "", religion: "", caste: "", gotram: "", rashi: "", nakshatram: "",
  birthPlace: "", aboutMe: "",
  // Career
  education: "", otherEducation: "", collegeType: "", collegeName: "",
  profession: "", otherProfession: "",
  companyName: "", workingPlace: "", annualIncome: "", otherInformation: "",
  // Family
  fatherName: "", fatherProfession: "", fatherDesignation: "",
  motherName: "", motherSurname: "", motherProfession: "",
  brothers: "", marriedBrothers: "", sisters: "", marriedSisters: "",
  familyFinancialStatus: "",
  // Preferences
  preferredBirthYearFrom: "", preferredBirthYearTo: "",
  preferredHeight: "", preferredEducation: "", preferredProfession: "",
  castePreference: "", preferredReligion: "", preferredMaritalStatus: "", preferredResidenceType: "",
  preferredLocation: "", otherExpectations: "",
  // Interests
  interests: [],
  // Address
  residenceType: "India",
  addressCountry: "India",
  state: "", district: "", cityTown: "", area: "", pincode: "",
  visaStatus: "", otherVisaStatus: "",
  nativePlace: "", presentResidence: "",
  // Contact
  mobileNumber: "", alternateNumber: "",
  imageUrls: [],
};

const requiredByStep = {
  0: ["fullName", "gender", "dateOfBirth", "maritalStatus", "religion", "caste", "height"],
  1: ["education", "profession", "annualIncome"],
  2: ["fatherName", "motherName", "familyFinancialStatus"],
  3: [],
  4: ["residenceType", "presentResidence"],
  5: [],
  6: ["mobileNumber"],
};

/* =====================================================================
   Options
===================================================================== */
const heightOptions = Array.from({ length: 25 }, (_, idx) => {
  const totalInches = 54 + idx;
  return `${Math.floor(totalInches / 12)}'${totalInches % 12}"`;
});

const SELECT = {
  gender: ["Male", "Female"],
  maritalStatus: ["Never Married", "Divorced", "Widowed", "Awaiting Divorce"],
  height: heightOptions,
  religion: ["Hindu", "Converted Christian"],
  caste: ["Goud / Gowda", "Settibalija", "Ediga", "Srisayana", "Yatha"],
  rashi: ["Mesha (మేషం)","Vrishabha (వృషభం)","Mithuna (మిథునం)","Karka (కర్కాటకం)","Simha (సింహం)","Kanya (కన్య)","Tula (తుల)","Vrischika (వృశ్చికం)","Dhanu (ధనుస్సు)","Makara (మకరం)","Kumbha (కుంభం)","Meena (మీనం)"],
  nakshatram: ["Ashwini (అశ్విని)","Bharani (భరణి)","Krittika (కృత్తిక)","Rohini (రోహిణి)","Mrigashira (మృగశిర)","Ardra (ఆర్ద్ర)","Punarvasu (పునర్వసు)","Pushya (పుష్యమి)","Ashlesha (ఆశ్లేష)","Magha (మఘ)","Purva Phalguni (పూర్వ ఫల్గుణి)","Uttara Phalguni (ఉత్తర ఫల్గుణి)","Hasta (హస్త)","Chitra (చిత్ర)","Swati (స్వాతి)","Vishakha (విశాఖ)","Anuradha (అనురాధ)","Jyeshtha (జ్యేష్ఠ)","Moola (మూల)","Purva Ashadha (పూర్వాషాఢ)","Uttara Ashadha (ఉత్తరాషాఢ)","Shravana (శ్రవణం)","Dhanishta (ధనిష్ఠ)","Shatabhisha (శతభిషం)","Purva Bhadrapada (పూర్వాభాద్ర)","Uttara Bhadrapada (ఉత్తరాభాద్ర)","Revati (రేవతి)"],
  collegeType: ["IIT","NIT","IIIT","IIM","AIIMS","BITS","Government University","Government College","Private University","Private College","Deemed University","Foreign University","Other"],
  education: ["10th","Intermediate / 12th","Diploma","ITI","B.A","B.Com","B.Sc","B.Tech / B.E","BBA","BCA","LLB","CA","CS","MBBS","BDS","B.Pharmacy","BVSC","M.A","M.Com","M.Sc","MS (Master of Science)","M.Tech / M.E","MBA","MCA","MVSC","MDS","MD / MS","Ph.D","Other"],
  profession: ["Software Engineer","Doctor","Nurse","Pharmacist","Engineer","Teacher / Lecturer","Professor","Government Employee","Banking Professional","Accountant","Chartered Accountant","Business Owner","Self Employed","Farmer / Agriculture","Lawyer","Police","Defence Personnel","Civil Services","Sales / Marketing","Designer","Architect","Consultant","Entrepreneur","Student","Homemaker","Not Working","Other"],
  visaStatus: ["Citizen","Permanent Resident","Green Card","Work Visa","Student Visa","Dependent Visa","Visitor Visa","Temporary Resident","Other"],
  annualIncome: ["Not Working","Below 2 Lakhs","2-5 Lakhs","5-10 Lakhs","10-15 Lakhs","15-25 Lakhs","25-50 Lakhs","50 Lakhs - 1 Crore","Above 1 Crore","Prefer not to say"],
  familyFinancialStatus: ["Lower Middle Class","Middle Class","Upper Middle Class","Rich","Prefer Not to Say"],
};

const interestOptions = [
  "Music","Travel","Cooking","Reading","Movies","Fitness","Devotional",
  "Photography","Dancing","Gardening","Sports","Art","Yoga","Foodie",
  "Tech","Outdoors","Pets",
];

const birthYearOptions = Array.from(
  { length: 43 }, (_, idx) => String(new Date().getFullYear() - 18 - idx)
);

/* =====================================================================
   Form atoms
===================================================================== */
const Field = ({ label, required, children, hint, fieldName, errorField: ef }) => (
  <div className="block" data-field={fieldName}>
    <label className={`mb-1.5 flex items-center gap-1 text-[13px] font-semibold ${ef && ef === fieldName ? "text-rose-600" : "text-ink-700"}`}>
      {label} {required && <span className="text-brand-600">*</span>}
    </label>
    <div className={ef && ef === fieldName ? "rounded-2xl ring-2 ring-rose-400" : ""}>
      {children}
    </div>
    {hint && <p className="mt-1 text-[11.5px] text-ink-400">{hint}</p>}
  </div>
);

const Input = ({ value, onChange, type = "text", placeholder, multiline, disabled, min, max }) => (
  multiline ? (
    <textarea
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={3}
      className="w-full resize-none rounded-2xl border border-sand-300 bg-white px-4 py-3.5 text-[14px] text-ink-800 outline-none transition focus:border-brand-600 focus:ring-4 focus:ring-brand-600/10"
      style={{ scrollMarginBottom: "200px" }}
    />
  ) : (
    <input
      type={type}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      min={min}
      max={max}
      className="input disabled:cursor-not-allowed disabled:bg-sand-100 disabled:text-ink-400"
      style={{ scrollMarginBottom: "200px" }}
    />
  )
);

const Select = ({ value, onChange, options, disabled, placeholder }) => (
  <select
    value={value || ""}
    onChange={(e) => onChange(e.target.value)}
    disabled={disabled}
    className="input disabled:cursor-not-allowed disabled:bg-sand-100 disabled:text-ink-400"
  >
    <option value="">{placeholder || "Select"}</option>
    {options.map((o) => <option key={o} value={o}>{o}</option>)}
  </select>
);
// Kept for backward compatibility — currently unused.
// eslint-disable-next-line no-unused-vars
const _UNUSED_Select = Select;

/* =====================================================================
   Main
===================================================================== */
const DRAFT_KEY = "karankiCreateProfileDraft";
const STEP_KEY = "karankiCreateProfileStep";

const CreateProfile = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isEditing = location.pathname === "/my-profile";
  const { user, loading: authLoading } = useAuthAccess();

  const [currentStep, setCurrentStep] = useState(() => {
    if (isEditing) return 0;
    const savedStep = Number(localStorage.getItem(STEP_KEY) || 0);
    return Number.isFinite(savedStep) ? Math.max(0, Math.min(savedStep, steps.length - 1)) : 0;
  });
  const [form, setForm] = useState(initialForm);
  const [profileId, setProfileId] = useState("");
  const [serverProfile, setServerProfile] = useState(null);
  const [imageFiles, setImageFiles] = useState([]);
  const [imageError, setImageError] = useState("");
  const [error, setError] = useState("");
  const [errorField, setErrorField] = useState("");
  const [notice, setNotice] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitState, setSubmitState] = useState("idle");
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const activeStepRef = useRef(null);
  const prevStepRef = useRef(currentStep);
  const savedFormRef = useRef(null);
  const formRef = useRef(form);
  const submitRedirectRef = useRef(null);
  formRef.current = form;
  const allCountries = useMemo(() => countriesList.slice(), []);

  const progress = Math.round(((currentStep + 1) / steps.length) * 100);

  // Scroll stepper tab into view only when step changes
  useEffect(() => {
    if (prevStepRef.current !== currentStep) {
      prevStepRef.current = currentStep;
      setTimeout(() => {
        activeStepRef.current?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
      }, 50);
    }
  }, [currentStep]);

  const hasUnsavedChanges = () => {
    if (!isEditing || !savedFormRef.current) return false;
    return JSON.stringify(form) !== JSON.stringify(savedFormRef.current) || imageFiles.length > 0;
  };

  const handleBackNavigation = () => {
    if (hasUnsavedChanges()) {
      setShowUnsavedModal(true);
    } else {
      navigate(-1);
    }
  };

  // ---------- restore draft / load profile ----------
  useEffect(() => {
    const loadDraft = async () => {
      const otpState = (() => {
        try { return JSON.parse(sessionStorage.getItem("karankiOtpState") || "null"); }
        catch { return null; }
      })();
      const savedAuth = JSON.parse(localStorage.getItem("karankiAuth") || "null");
      const savedPhone = otpState?.phone || savedAuth?.phone || localStorage.getItem("karankiProfilePhone");

      if (authLoading) {
        return;
      }

      if (!user || (!savedPhone && !isEditing)) {
        navigate("/register");
        return;
      }

      const id = user.uid;
      setProfileId(id);
      if (savedPhone) localStorage.setItem("karankiProfilePhone", savedPhone);

      try {
        const profile = await findProfileForUser(db, user, savedPhone);
        let localDraft = null;
        if (!isEditing) {
          try {
            const raw = localStorage.getItem(DRAFT_KEY);
            if (raw) localDraft = JSON.parse(raw);
          } catch {
            localDraft = null;
          }
        }

        const serverData = profile ? { ...profile, imageUrls: getProfileImages(profile) } : null;
        if (serverData) setServerProfile(serverData);

        const base = {
          ...initialForm,
          fullName: otpState?.name || initialForm.fullName,
          profileType: otpState?.profileType || initialForm.profileType,
          mobileNumber: savedPhone || "",
        };

        const sourceForServer = (data) => {
          const educationIsCustom = data.education && !SELECT.education.includes(data.education);
          const professionIsCustom = data.profession && !SELECT.profession.includes(data.profession);
          const visaIsCustom = data.visaStatus && !SELECT.visaStatus.includes(data.visaStatus);
          return {
            ...base,
            ...data,
            education: educationIsCustom ? "Other" : data.education || "",
            otherEducation: data.otherEducation || (educationIsCustom ? data.education : ""),
            profession: professionIsCustom ? "Other" : data.profession || "",
            otherProfession: data.otherProfession || (professionIsCustom ? data.profession : ""),
            visaStatus: visaIsCustom ? "Other" : data.visaStatus || "",
            otherVisaStatus: data.otherVisaStatus || (visaIsCustom ? data.visaStatus : ""),
          };
        };

        if (isEditing && serverData) {
          const loaded = sourceForServer(serverData);
          setForm(loaded);
          savedFormRef.current = loaded;
          setCurrentStep(0);
          setNotice("");
        } else if (localDraft && localDraft.mobileNumber && savedPhone && String(localDraft.mobileNumber).includes(String(savedPhone).slice(-10))) {
          setForm({ ...base, ...localDraft });
          setNotice("Welcome back — your draft is restored.");
        } else if (serverData) {
          setForm(sourceForServer(serverData));
          setCurrentStep(Math.min(Number(serverData.currentStep) || 0, steps.length - 1));
        } else {
          setForm(base);
        }
      } catch (err) {
        setError(err?.message || "Could not load saved draft.");
      } finally {
        setLoading(false);
      }
    };

    loadDraft();
  }, [isEditing, navigate, user, authLoading]);

  // ---------- auto-save draft locally (only during create flow) ----------
  useEffect(() => {
    if (loading || isEditing) return;
    const id = setTimeout(() => {
      try {
        localStorage.setItem(DRAFT_KEY, JSON.stringify(formRef.current));
        localStorage.setItem(STEP_KEY, String(currentStep));
      } catch { /* ignore */ }
    }, 250);
    return () => clearTimeout(id);
  }, [form, currentStep, loading, isEditing]);

  useEffect(() => {
    return () => {
      imageFiles.forEach((it) => URL.revokeObjectURL(it.preview));
    };
  }, [imageFiles]);

  /* =================================================================
     Field handlers
  ================================================================= */
  const updateField = (field, value) => {
    setForm((prev) => {
      // cascading address fields
      if (field === "residenceType") {
        return {
          ...prev,
          residenceType: value,
          addressCountry: value === "India" ? "India" : "",
          state: "", district: "", cityTown: "", area: "", pincode: "",
          visaStatus: value === "India" ? "" : prev.visaStatus,
          otherVisaStatus: value === "India" ? "" : prev.otherVisaStatus,
          nativePlace: "", presentResidence: "",
        };
      }
      if (field === "addressCountry") {
        return {
          ...prev,
          addressCountry: value,
          state: "", district: "", cityTown: "",
          nativePlace: "",
        };
      }
      if (field === "state") {
        return { ...prev, state: value, district: "", cityTown: "", nativePlace: "" };
      }
      if (field === "district") {
        return { ...prev, district: value, cityTown: "", nativePlace: "" };
      }
      if (field === "cityTown") {
        return {
          ...prev,
          cityTown: value,
          nativePlace: value,
        };
      }
      if (field === "preferredBirthYearFrom") {
        return {
          ...prev,
          preferredBirthYearFrom: value,
          preferredBirthYearTo: prev.preferredBirthYearTo && value && Number(prev.preferredBirthYearTo) < Number(value) ? "" : prev.preferredBirthYearTo,
        };
      }
      if (field === "preferredBirthYearTo") {
        return {
          ...prev,
          preferredBirthYearTo: value,
          preferredBirthYearFrom: prev.preferredBirthYearFrom && value && Number(prev.preferredBirthYearFrom) > Number(value) ? "" : prev.preferredBirthYearFrom,
        };
      }
      if (field === "brothers") {
        const total = Number(value) || 0;
        return {
          ...prev,
          brothers: value,
          marriedBrothers: total > 0 ? String(Math.min(Number(prev.marriedBrothers) || 0, total)) : "",
        };
      }
      if (field === "sisters") {
        const total = Number(value) || 0;
        return {
          ...prev,
          sisters: value,
          marriedSisters: total > 0 ? String(Math.min(Number(prev.marriedSisters) || 0, total)) : "",
        };
      }
      if (field === "marriedBrothers") {
        return { ...prev, marriedBrothers: String(Math.min(Number(value) || 0, Number(prev.brothers) || 0)) };
      }
      if (field === "marriedSisters") {
        return { ...prev, marriedSisters: String(Math.min(Number(value) || 0, Number(prev.sisters) || 0)) };
      }
      return { ...prev, [field]: value };
    });
    setError(""); setNotice("");
  };

  const toggleInterest = (i) => {
    const selected = form.interests.includes(i);
    updateField("interests", selected ? form.interests.filter((x) => x !== i) : [...form.interests, i]);
  };

  /* =================================================================
     Validation
  ================================================================= */
  const fieldLabels = {
    fullName: "Full name", gender: "Gender", dateOfBirth: "Date of birth",
    maritalStatus: "Marital status", religion: "Religion", caste: "Caste", height: "Height",
    education: "Education", profession: "Profession", collegeType: "College type",
    annualIncome: "Annual income",
    fatherName: "Father name", motherName: "Mother name",
    familyFinancialStatus: "Family financial status",
    mobileNumber: "Mobile number", residenceType: "Residence type",
    addressCountry: "Country", state: "State", district: "District", presentResidence: "Present residence",
  };
  const validate = () => {
    const required = [...(requiredByStep[currentStep] || [])];
    if (currentStep === 4) {
      const isIndia = (form.residenceType || "India") === "India";
      required.push(...(isIndia ? ["state", "district"] : ["addressCountry", "state"]));
    }
    // Conditionally require collegeType when education is Diploma or higher
    if (currentStep === 1 && form.education && form.education !== "10th" && form.education !== "Intermediate / 12th") {
      required.push("collegeType");
    }
    const missing = required.find((f) => !String(form[f] || "").trim());
    if (missing) {
      const label = fieldLabels[missing] || missing;
      setErrorField(missing);
      setError(`${label} is required.`);
      toast.error(`${label} is required`);
      setTimeout(() => {
        const el = document.querySelector(`[data-field="${missing}"]`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 100);
      return false;
    }

    // Age check — must be 18+
    if (currentStep === 0 && form.dateOfBirth) {
      const dob = new Date(form.dateOfBirth);
      const today = new Date();
      const age = today.getFullYear() - dob.getFullYear() -
        (today < new Date(today.getFullYear(), dob.getMonth(), dob.getDate()) ? 1 : 0);
      if (age < 18) {
        setErrorField("dateOfBirth");
        setError("You must be at least 18 years old to register.");
        toast.error("You must be at least 18 years old to register");
        setTimeout(() => {
          const el = document.querySelector('[data-field="dateOfBirth"]');
          if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 100);
        return false;
      }
    }

    setErrorField("");
    return true;
  };

  /* =================================================================
     Save flow
  ================================================================= */
  const calculateCompletion = (data, completed) => {
    const tracked = Object.keys(initialForm).filter((k) => k !== "imageUrls");
    const filled = tracked.filter((k) => {
      const v = data[k];
      return Array.isArray(v) ? v.length > 0 : Boolean(String(v || "").trim());
    }).length;
    return Math.min(100, Math.round((filled / tracked.length) * 70) + Math.round(((completed + 1) / steps.length) * 30));
  };

  const uploadImages = async () => {
    // Resolve current user from auth directly (avoids stale hook state)
    const currentUser = auth.currentUser || user;
    if (!currentUser) throw new Error("Login expired. Please log in again.");

    // Force a token refresh so Firebase Storage accepts the upload
    try {
      await currentUser.getIdToken(true);
    } catch {
      // If refresh fails, try with the existing token
    }

    const urls = [];
    for (const item of imageFiles) {
      // Compress image before upload (reduces 3-5MB to ~200-400KB)
      const compressed = await compressImage(item.file).catch(() => item.file);
      const safeName = item.file.name.replace(/[^a-zA-Z0-9.-]/g, "_");
      const r = ref(storage, `profile-images/${currentUser.uid}/${Date.now()}-${safeName}`);
      await uploadBytes(r, compressed);
      urls.push(await getDownloadURL(r));
    }
    return [...(form.imageUrls || []), ...urls];
  };

  const persistToFirestore = async (status, nextStep, imageUrls) => {
    // Use auth.currentUser as the reliable source of uid
    const currentUid = auth.currentUser?.uid || user?.uid || profileId;
    if (!currentUid) throw new Error("Login expired. Please log in again.");

    const completion = status === "Pending Verification" ? 100 : calculateCompletion(form, nextStep);
    const education = form.education === "Other" ? form.otherEducation : form.education;
    const profession = form.profession === "Other" ? form.otherProfession : form.profession;
    const visaStatus = form.visaStatus === "Other" ? form.otherVisaStatus : form.visaStatus;
    const currentYear = new Date().getFullYear();
    const minAge = form.preferredBirthYearTo ? currentYear - Number(form.preferredBirthYearTo) : "";
    const maxAge = form.preferredBirthYearFrom ? currentYear - Number(form.preferredBirthYearFrom) : "";
    const preferredAgeRange = minAge && maxAge ? `${minAge} - ${maxAge}` : minAge ? String(minAge) : maxAge ? String(maxAge) : "";

    // Get the KM Profile ID from localStorage (set during OTP verification)
    const savedAuth = JSON.parse(localStorage.getItem("karankiAuth") || "null");
    const kmProfileId = form.kmProfileId || savedAuth?.profileId || "";

    await setDoc(
      doc(db, "profiles", currentUid),
      {
        ...form,
        education: education || "",
        profession: profession || "",
        visaStatus: form.residenceType === "NRI" ? visaStatus || "" : "",
        otherVisaStatus: form.residenceType === "NRI" ? form.otherVisaStatus || "" : "",
        preferredAgeRange,
        imageUrls,
        profileId: currentUid,
        kmProfileId,
        uid: currentUid,
        role: "user",
        status: form.status || "active",
        membership: form.membership || "free",
        currentStep: nextStep,
        profileStatus: status,
        profileCompletion: completion,
        updatedAt: serverTimestamp(),
        createdAt: form.createdAt || serverTimestamp(),
      },
      { merge: true }
    );
  };

  // Save & continue inside CREATE flow → just advances step (auto-saved locally)
  const handleNext = () => {
    if (!validate()) return;
    setCurrentStep((s) => Math.min(s + 1, steps.length - 1));
    setNotice("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handlePrev = () => {
    setCurrentStep((s) => Math.max(0, s - 1));
    setError(""); setNotice("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);
    setSubmitState("registering");
    try {
      const imageUrls = await uploadImages();
      const currentUid = auth.currentUser?.uid || user?.uid || profileId;
      if (!currentUid) throw new Error("Login expired. Please log in again.");

      await persistToFirestore("Pending Verification", steps.length - 1, imageUrls);

      const savedAuth = JSON.parse(localStorage.getItem("karankiAuth") || "null");
      const nextAuth = {
        ...savedAuth,
        uid: currentUid,
        role: savedAuth?.role || "user",
        phone: savedAuth?.phone || form.mobileNumber || localStorage.getItem("karankiProfilePhone") || "",
        profileId: savedAuth?.profileId || currentUid,
        idToken: savedAuth?.idToken || "",
        sessionStartedAt: savedAuth?.sessionStartedAt || new Date().toISOString(),
      };
      localStorage.setItem("karankiAuth", JSON.stringify(nextAuth));
      localStorage.setItem("karankiProfilePhone", nextAuth.phone || "");
      sessionStorage.setItem(
        "karankiProfileJustSubmitted",
        JSON.stringify({ uid: currentUid, at: Date.now() })
      );
      primeAuthCache(currentUid, {
        adminRecord: null,
        profile: {
          ...form,
          id: currentUid,
          uid: currentUid,
          profileId: currentUid,
          mobileNumber: nextAuth.phone || form.mobileNumber || "",
          imageUrls,
          currentStep: steps.length - 1,
          profileStatus: "Pending Verification",
          status: form.status || "active",
          membership: form.membership || "free",
        },
      });
      sessionStorage.removeItem("karankiOtpState");
      // clear local draft after submission
      localStorage.removeItem(DRAFT_KEY);
      localStorage.removeItem(STEP_KEY);
      setSubmitState("success");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      if (submitRedirectRef.current) {
        clearTimeout(submitRedirectRef.current);
        submitRedirectRef.current = null;
      }
      setSubmitState("idle");
      setError(err?.message || "Could not complete profile.");
    } finally {
      setSaving(false);
    }
  };

  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // Editing → save changes
  const handleSaveChanges = async () => {
    setSaving(true);
    try {
      const imageUrls = await uploadImages();
      const status = isVerifiedProfile(serverProfile)
        ? serverProfile.profileStatus
        : "Pending Verification";
      await persistToFirestore(status, currentStep, imageUrls);
      invalidateAuthCache();
      setImageFiles([]);
      setNotice("");
      setError("");
      savedFormRef.current = { ...form };
      setShowSuccessModal(true);
    } catch (err) {
      setError(err?.message || "Could not save changes.");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    return () => {
      if (submitRedirectRef.current) {
        clearTimeout(submitRedirectRef.current);
      }
    };
  }, []);

  /* =================================================================
     Image handlers
  ================================================================= */
  const handleImages = (files) => {
    setImageError("");
    const valid = [];
    for (const file of Array.from(files)) {
      if (!file.type.startsWith("image/")) { setImageError("Only images allowed."); continue; }
      if (file.size > 5 * 1024 * 1024) { setImageError("Each image must be 5 MB or smaller."); continue; }
      valid.push({ file, preview: URL.createObjectURL(file), id: `${file.name}-${file.lastModified}-${Math.random()}` });
    }
    setImageFiles((prev) => [...prev, ...valid].slice(0, 6));
  };
  const removeImage = (id) => {
    setImageFiles((prev) => {
      const removed = prev.find((it) => it.id === id);
      if (removed) URL.revokeObjectURL(removed.preview);
      return prev.filter((it) => it.id !== id);
    });
  };
  const removeUploadedImage = async (url) => {
    updateField("imageUrls", (form.imageUrls || []).filter((u) => u !== url));
    // Delete from Firebase Storage
    try {
      const imageRef = ref(storage, url);
      await deleteObject(imageRef);
    } catch {
      // ignore — URL might not be a direct storage ref
    }
  };

  const setAsDefaultUploaded = (url) => {
    const current = form.imageUrls || [];
    if (current[0] === url) return; // already default
    const reordered = [url, ...current.filter((u) => u !== url)];
    updateField("imageUrls", reordered);
  };

  /* =================================================================
     Step content
  ================================================================= */
  const lockedAlways = ["mobileNumber"];
  const lockedOnEdit = ["gender", "dateOfBirth"];
  const isLocked = (name) => lockedAlways.includes(name) || (isEditing && lockedOnEdit.includes(name));

  const renderStep = () => {
    if (currentStep === 0) {
      return (
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Full name" required>
            <Input value={form.fullName} onChange={(v) => updateField("fullName", v)} placeholder="Enter your full name" disabled={isLocked("fullName")} />
          </Field>
          <Field label="Gender" required>
            <Combobox value={form.gender} onChange={(v) => updateField("gender", v)} options={SELECT.gender} disabled={isLocked("gender")} placeholder="Select gender" searchPlaceholder="Search gender..." />
          </Field>
          <Field label="Marital status" required>
            <Combobox value={form.maritalStatus} onChange={(v) => updateField("maritalStatus", v)} options={SELECT.maritalStatus} placeholder="Select status" searchPlaceholder="Search status..." />
          </Field>
          <Field label="Date of birth" required fieldName="dateOfBirth" errorField={errorField}>
            <Input
              type="date"
              value={form.dateOfBirth}
              onChange={(v) => updateField("dateOfBirth", v)}
              disabled={isLocked("dateOfBirth")}
              max={(() => {
                const d = new Date();
                d.setFullYear(d.getFullYear() - 18);
                return d.toISOString().split("T")[0];
              })()}
            />
          </Field>
          <Field label="Time of birth">
            <Input type="time" value={form.timeOfBirth} onChange={(v) => updateField("timeOfBirth", v)} />
          </Field>
          <Field label="Height" required>
            <Combobox value={form.height} onChange={(v) => updateField("height", v)} options={SELECT.height} placeholder="Select height" searchPlaceholder="Search height..." />
          </Field>
          <Field label="Religion" required>
            <Combobox value={form.religion} onChange={(v) => updateField("religion", v)} options={SELECT.religion} placeholder="Select religion" searchPlaceholder="Search religion..." />
          </Field>
          <Field label="Caste" required>
            <Combobox value={form.caste} onChange={(v) => updateField("caste", v)} options={SELECT.caste} placeholder="Select caste" searchPlaceholder="Search caste..." />
          </Field>
          <Field label="Gotram">
            <Input value={form.gotram} onChange={(v) => updateField("gotram", v)} placeholder="e.g. Bharadwaja" />
          </Field>
          <Field label="Rashi">
            <Combobox value={form.rashi} onChange={(v) => updateField("rashi", v)} options={SELECT.rashi} placeholder="Select rashi" searchPlaceholder="Search rashi..." />
          </Field>
          <Field label="Nakshatram">
            <Combobox value={form.nakshatram} onChange={(v) => updateField("nakshatram", v)} options={SELECT.nakshatram} placeholder="Select nakshatram" searchPlaceholder="Search nakshatram..." />
          </Field>
          <Field label="Birth place">
            <Input value={form.birthPlace} onChange={(v) => updateField("birthPlace", v)} placeholder="City, state" />
          </Field>
          <div className="sm:col-span-2">
            <Field label="About me" hint="A short, warm introduction. Visible to potential matches.">
              <Input value={form.aboutMe} onChange={(v) => updateField("aboutMe", v)} multiline placeholder="Tell something about yourself, your values, hobbies and aspirations..." />
            </Field>
          </div>
        </div>
      );
    }

    if (currentStep === 1) {
      return (
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Education" required>
            <Combobox value={form.education} onChange={(v) => updateField("education", v)} options={SELECT.education} placeholder="Select education" searchPlaceholder="Search education..." />
          </Field>
          {form.education === "Other" && (
            <Field label="Other education">
              <Input value={form.otherEducation} onChange={(v) => updateField("otherEducation", v)} placeholder="Enter education" />
            </Field>
          )}
          {form.education && form.education !== "10th" && form.education !== "Intermediate / 12th" && (
            <>
              <Field label="College type" required>
                <Combobox value={form.collegeType} onChange={(v) => updateField("collegeType", v)} options={SELECT.collegeType} placeholder="Select college type" searchPlaceholder="Search type..." />
              </Field>
              <Field label="College / University name">
                <Input value={form.collegeName} onChange={(v) => updateField("collegeName", v)} placeholder="Where did you study?" />
              </Field>
            </>
          )}
          <Field label="Profession" required>
            <Combobox value={form.profession} onChange={(v) => updateField("profession", v)} options={SELECT.profession} placeholder="Select profession" searchPlaceholder="Search profession..." />
          </Field>
          {form.profession === "Other" && (
            <Field label="Other profession">
              <Input value={form.otherProfession} onChange={(v) => updateField("otherProfession", v)} placeholder="Enter profession" />
            </Field>
          )}
          <Field label="Company name">
            <Input value={form.companyName} onChange={(v) => updateField("companyName", v)} placeholder="Company name" />
          </Field>
          <Field label="Working place">
            <Input value={form.workingPlace} onChange={(v) => updateField("workingPlace", v)} placeholder="City of work" />
          </Field>
          <Field label="Annual income" required>
            <Combobox value={form.annualIncome} onChange={(v) => updateField("annualIncome", v)} options={SELECT.annualIncome} placeholder="Select range" searchPlaceholder="Search income range..." />
          </Field>
          <div className="sm:col-span-2">
            <Field label="Other information" hint="Any additional details you'd like matches to know.">
              <Input value={form.otherInformation} onChange={(v) => updateField("otherInformation", v)} multiline placeholder="Optional" />
            </Field>
          </div>
        </div>
      );
    }

    if (currentStep === 2) {
      return (
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Father name" required><Input value={form.fatherName} onChange={(v) => updateField("fatherName", v)} /></Field>
          <Field label="Father profession"><Input value={form.fatherProfession} onChange={(v) => updateField("fatherProfession", v)} /></Field>
          <Field label="Father designation"><Input value={form.fatherDesignation} onChange={(v) => updateField("fatherDesignation", v)} /></Field>
          <Field label="Mother name" required><Input value={form.motherName} onChange={(v) => updateField("motherName", v)} /></Field>
          <Field label="Mother surname"><Input value={form.motherSurname} onChange={(v) => updateField("motherSurname", v)} /></Field>
          <Field label="Mother profession"><Input value={form.motherProfession} onChange={(v) => updateField("motherProfession", v)} /></Field>
          <Field label="Number of brothers"><Input type="number" min={0} value={form.brothers} onChange={(v) => updateField("brothers", v)} /></Field>
          {Number(form.brothers) > 0 && (
            <Field label="Married brothers"><Input type="number" min={0} max={Number(form.brothers)} value={form.marriedBrothers} onChange={(v) => updateField("marriedBrothers", v)} /></Field>
          )}
          <Field label="Number of sisters"><Input type="number" min={0} value={form.sisters} onChange={(v) => updateField("sisters", v)} /></Field>
          {Number(form.sisters) > 0 && (
            <Field label="Married sisters"><Input type="number" min={0} max={Number(form.sisters)} value={form.marriedSisters} onChange={(v) => updateField("marriedSisters", v)} /></Field>
          )}
          <Field label="Family financial status" required>
            <Combobox value={form.familyFinancialStatus} onChange={(v) => updateField("familyFinancialStatus", v)} options={SELECT.familyFinancialStatus} placeholder="Select status" searchPlaceholder="Search status..." />
          </Field>
        </div>
      );
    }

    if (currentStep === 5) {
      const educationPref = [...SELECT.education.filter((o) => o !== "Other"), "Any"];
      const professionPref = [...SELECT.profession.filter((o) => o !== "Other"), "Any"];
      const castePref = [...SELECT.caste, "Any"];
      const maritalPref = [...SELECT.maritalStatus, "Any"];
      const residenceTypePref = ["India", "NRI", "Any"];

      return (
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Birth year from" hint="Partner's birth year (older limit)">
            <Combobox value={form.preferredBirthYearFrom} onChange={(v) => updateField("preferredBirthYearFrom", v)} options={birthYearOptions} placeholder="Any" searchPlaceholder="Search year..." />
          </Field>
          <Field label="Birth year to" hint="Partner's birth year (younger limit)">
            <Combobox
              value={form.preferredBirthYearTo}
              onChange={(v) => updateField("preferredBirthYearTo", v)}
              options={birthYearOptions.filter((y) => !form.preferredBirthYearFrom || Number(y) >= Number(form.preferredBirthYearFrom))}
              placeholder="Any"
              searchPlaceholder="Search year..."
            />
          </Field>
          <Field label="Marital status">
            <Combobox value={form.preferredMaritalStatus} onChange={(v) => updateField("preferredMaritalStatus", v)} options={maritalPref} placeholder="Any" searchPlaceholder="Search status..." />
          </Field>
          <Field label="Education">
            <Combobox value={form.preferredEducation} onChange={(v) => updateField("preferredEducation", v)} options={educationPref} placeholder="Any" searchPlaceholder="Search education..." />
          </Field>
          <Field label="Profession">
            <Combobox value={form.preferredProfession} onChange={(v) => updateField("preferredProfession", v)} options={professionPref} placeholder="Any" searchPlaceholder="Search profession..." />
          </Field>
          <Field label="Caste">
            <Combobox value={form.castePreference} onChange={(v) => updateField("castePreference", v)} options={castePref} placeholder="Any" searchPlaceholder="Search caste..." />
          </Field>
          <Field label="Religion">
            <Combobox value={form.preferredReligion} onChange={(v) => updateField("preferredReligion", v)} options={[...SELECT.religion, "Any"]} placeholder="Any" searchPlaceholder="Search religion..." />
          </Field>
          <Field label="Residence type">
            <Combobox value={form.preferredResidenceType} onChange={(v) => updateField("preferredResidenceType", v)} options={residenceTypePref} placeholder="Any" searchPlaceholder="Search type..." />
          </Field>
          <div className="sm:col-span-2">
            <Field label="Preferred location" hint="City or state name (free text)">
              <Input value={form.preferredLocation} onChange={(v) => updateField("preferredLocation", v)} placeholder="e.g. Hyderabad, Bangalore" />
            </Field>
          </div>
          <div className="sm:col-span-2">
            <Field label="Other expectations">
              <Input value={form.otherExpectations} onChange={(v) => updateField("otherExpectations", v)} multiline placeholder="Optional" />
            </Field>
          </div>
        </div>
      );
    }

    if (currentStep === 3) {
      return (
        <div>
          <p className="text-[13px] text-ink-500">Pick the things you enjoy. Helps us improve your matches.</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {interestOptions.map((it) => {
              const selected = form.interests.includes(it);
              return (
                <button
                  key={it}
                  type="button"
                  onClick={() => toggleInterest(it)}
                  className={`rounded-full border px-3.5 py-1.5 text-[12.5px] font-semibold transition ${
                    selected
                      ? "border-brand-600 bg-brand-gradient text-white shadow-soft"
                      : "border-sand-300 bg-white text-ink-700 hover:border-brand-600 hover:text-brand-600"
                  }`}
                >
                  {selected && <Check size={11} className="mr-1 inline-block" />}
                  {it}
                </button>
              );
            })}
          </div>
        </div>
      );
    }

    if (currentStep === 4) {
      const isIndia = (form.residenceType || "India") === "India";
      const indiaDistricts = Object.keys(indiaAddressOptions[form.state] || {});

      return (
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Residence type" required>
            <Combobox value={form.residenceType} onChange={(v) => updateField("residenceType", v)} options={residenceTypes} placeholder="Select type" searchPlaceholder="Search type..." />
          </Field>

          {isIndia ? (
            <Field label="Country">
              <Input value="India" disabled />
            </Field>
          ) : (
            <Field label="Country" required fieldName="addressCountry" errorField={errorField} hint="Type to search 195+ countries">
              <Combobox
                value={form.addressCountry}
                onChange={(v) => updateField("addressCountry", v)}
                options={allCountries}
                placeholder="Select country"
                searchPlaceholder="Search country..."
              />
            </Field>
          )}

          {isIndia ? (
            <>
              <Field label="State" required hint="Search and select your state">
                <Combobox
                  value={form.state}
                  onChange={(v) => updateField("state", v)}
                  options={indiaStates}
                  placeholder="Select state"
                  searchPlaceholder="Search state..."
                />
              </Field>
              <Field label="District" required fieldName="district" errorField={errorField} hint={form.state ? "Search and select your district" : "Select state first"}>
                <Combobox
                  value={form.district}
                  onChange={(v) => updateField("district", v)}
                  options={indiaDistricts}
                  placeholder={form.state ? "Select district" : "Pick a state first"}
                  searchPlaceholder="Search district..."
                  disabled={!form.state}
                />
              </Field>
              <Field label="City / Town / Village">
                <Input
                  value={form.cityTown}
                  onChange={(v) => updateField("cityTown", v)}
                  placeholder="Enter your city, town or village"
                />
              </Field>
              <Field label="Area / Locality">
                <Input value={form.area} onChange={(v) => updateField("area", v)} placeholder="Optional" />
              </Field>
              <Field label="Pincode">
                <Input type="number" value={form.pincode} onChange={(v) => updateField("pincode", v)} placeholder="Optional" />
              </Field>
            </>
          ) : (
            <>
              <Field label="State / Province" required fieldName="state" errorField={errorField}>
                <Input value={form.state} onChange={(v) => updateField("state", v)} placeholder="Enter state / province" disabled={!form.addressCountry} />
              </Field>
              <Field label="City">
                <Input value={form.cityTown} onChange={(v) => updateField("cityTown", v)} placeholder="Enter city" disabled={!form.addressCountry} />
              </Field>
              <Field label="Visa status">
                <Combobox value={form.visaStatus} onChange={(v) => updateField("visaStatus", v)} options={SELECT.visaStatus} placeholder="Select status" searchPlaceholder="Search visa status..." />
              </Field>
              {form.visaStatus === "Other" && (
                <Field label="Other visa status">
                  <Input value={form.otherVisaStatus} onChange={(v) => updateField("otherVisaStatus", v)} placeholder="Enter status" />
                </Field>
              )}
            </>
          )}

          <div className="sm:col-span-2">
            <Field label="Present residence" required hint="Enter your full current address or location.">
              <Input value={form.presentResidence} onChange={(v) => updateField("presentResidence", v)} placeholder="Enter Present Residence" />
            </Field>
          </div>
        </div>
      );
    }

    // Contact + photos (final step)
    return (
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Mobile number" required>
          <Input value={form.mobileNumber} onChange={(v) => updateField("mobileNumber", v)} disabled />
        </Field>
        <Field label="Alternate number">
          <Input value={form.alternateNumber} onChange={(v) => updateField("alternateNumber", v)} placeholder="Alternate number" />
        </Field>

        <div className="sm:col-span-2">
          <Field label="Profile photos" hint="Upload up to 6 photos. Tap the star to set your default (first visible) photo.">
            <label
              className="flex min-h-32 cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed border-sand-300 bg-white px-4 py-6 text-center transition hover:border-brand-600"
              onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("border-brand-600", "bg-brand-50"); }}
              onDragLeave={(e) => { e.currentTarget.classList.remove("border-brand-600", "bg-brand-50"); }}
              onDrop={(e) => {
                e.preventDefault();
                e.currentTarget.classList.remove("border-brand-600", "bg-brand-50");
                handleImages(e.dataTransfer.files);
              }}
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
                <ImagePlus size={22} />
              </div>
              <span className="mt-3 font-display text-[14px] font-bold text-ink-800">
                Drop photos here or tap to upload
              </span>
              <span className="mt-1 text-[11.5px] text-ink-400">Up to 6 photos · JPG / PNG / WEBP up to 5 MB each</span>
              <input
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => handleImages(e.target.files)}
              />
            </label>
            {imageError && <p className="mt-2 text-[12.5px] text-rose-600">{imageError}</p>}
            {(imageFiles.length > 0 || form.imageUrls?.length > 0) && (
              <>
                <p className="mt-3 flex items-center gap-1.5 text-[11.5px] text-ink-500">
                  <Camera size={12} className="text-brand-600" />
                  Tap the camera icon on any photo to set it as your profile picture.
                </p>
                <div className="mt-2 grid grid-cols-3 gap-2 sm:grid-cols-6">
                  {form.imageUrls?.map((url, idx) => {
                    const isDefault = idx === 0;
                    return (
                      <div key={url} className={`relative rounded-2xl ${isDefault ? "ring-2 ring-brand-600 ring-offset-2" : "ring-1 ring-sand-200"}`}>
                        <img src={url} alt="Profile" className="aspect-square w-full rounded-2xl object-cover object-center" />
                        {/* Set as default */}
                        <button
                          type="button"
                          onClick={() => setAsDefaultUploaded(url)}
                          title={isDefault ? "This is your default photo" : "Set as default photo"}
                          className={`absolute left-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full shadow-soft transition ${
                            isDefault
                              ? "bg-brand-600 text-white"
                              : "bg-white/90 text-ink-400 hover:bg-amber-50 hover:text-amber-600"
                          }`}
                        >
                          <Camera size={12} />
                        </button>
                        {/* Remove */}
                        <button
                          type="button"
                          onClick={() => removeUploadedImage(url)}
                          className="absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-white text-rose-600 shadow-soft hover:bg-rose-50"
                        >
                          <X size={12} />
                        </button>
                        {isDefault && (
                          <span className="absolute inset-x-0 bottom-0 rounded-b-2xl bg-brand-600/90 py-0.5 text-center text-[9px] font-bold uppercase tracking-wider text-white">
                            Default
                          </span>
                        )}
                      </div>
                    );
                  })}
                  {imageFiles.map((it, idx) => {
                    const globalIdx = (form.imageUrls?.length || 0) + idx;
                    const isDefault = globalIdx === 0 && !(form.imageUrls?.length);
                    return (
                      <div key={it.id} className={`relative rounded-2xl ${isDefault ? "ring-2 ring-brand-600 ring-offset-2" : "ring-1 ring-sand-200"}`}>
                        <img src={it.preview} alt="Preview" className="aspect-square w-full rounded-2xl object-cover object-center" />
                        <button
                          type="button"
                          onClick={() => removeImage(it.id)}
                          className="absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-white text-rose-600 shadow-soft hover:bg-rose-50"
                        >
                          <X size={12} />
                        </button>
                        {isDefault && (
                          <span className="absolute inset-x-0 bottom-0 rounded-b-2xl bg-brand-600/90 py-0.5 text-center text-[9px] font-bold uppercase tracking-wider text-white">
                            Default
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </Field>
        </div>
      </div>
    );
  };

  /* =================================================================
     Render
  ================================================================= */
  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <main className="page-container pt-4 pb-app-nav sm:pt-6 lg:pt-8">
          <div className="card-elevated p-6 sm:p-10">
            <div className="skeleton h-4 w-24 rounded-full" />
            <div className="mt-3 skeleton h-9 w-2/3" />
            <div className="mt-4 skeleton h-2 w-full" />
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i}>
                  <div className="skeleton h-3 w-24" />
                  <div className="mt-2 skeleton h-12" />
                </div>
              ))}
            </div>
          </div>
        </main>
      </div>
    );
  }

  const StepIcon = steps[currentStep]?.Icon || User;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <main className="page-container pt-3 pb-28 sm:pt-6 sm:pb-28 lg:pt-8 lg:pb-28">
        {isEditing ? (
          /* Compact header for /my-profile (no hero card) */
          <div className="mb-3 sm:mb-5">
            <div className="flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={handleBackNavigation}
                className="inline-flex items-center gap-1.5 rounded-2xl border border-sand-200 bg-white px-3 py-2 text-[12.5px] font-semibold text-ink-700 shadow-soft transition hover:border-brand-600 hover:text-brand-600"
              >
                <ChevronLeft size={14} /> Back
              </button>
              <div className="rounded-full bg-brand-gradient px-3 py-1 text-[11.5px] font-bold text-white shadow-soft">
                {progress}%
              </div>
            </div>
            <div className="mt-3">
              <h1 className="font-display text-xl font-bold text-ink-800 sm:text-2xl">Edit your profile</h1>
              <p className="mt-0.5 text-[12.5px] text-ink-400 sm:text-[13.5px]">
                Step {currentStep + 1} of {steps.length} · {steps[currentStep]?.hint}
              </p>
              <div className="mt-2.5 h-1.5 overflow-hidden rounded-full bg-sand-200">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.5, ease: "easeOut" }}
                  className="h-full rounded-full bg-brand-gradient"
                />
              </div>
            </div>
          </div>
        ) : (
          /* Hero only for /create-profile */
          <motion.section
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="relative overflow-hidden rounded-3xl bg-brand-gradient p-4 text-white shadow-premium sm:p-7"
          >
            <div className="pointer-events-none absolute -right-12 -top-12 h-40 w-40 rounded-full bg-white/10 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-16 -left-12 h-40 w-40 rounded-full bg-amber-200/20 blur-3xl" />
            <div className="relative flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <span className="inline-flex items-center gap-1.5 rounded-full border border-white/25 bg-white/10 px-2.5 py-1 text-[10.5px] font-semibold backdrop-blur sm:text-[11.5px]">
                  <Sparkles size={11} /> Step {currentStep + 1} of {steps.length}
                </span>
                <h1 className="mt-2.5 font-display text-xl font-bold leading-tight sm:mt-3 sm:text-3xl">
                  Let's create your profile
                </h1>
                <p className="mt-0.5 text-[12.5px] text-white/85 sm:mt-1 sm:text-[13.5px]">
                  {steps[currentStep]?.hint}
                </p>
              </div>
              <div className="flex items-center justify-between gap-3 sm:justify-end">
                <div className="hidden h-14 w-14 items-center justify-center rounded-2xl bg-white/15 text-white sm:inline-flex">
                  <StepIcon size={22} />
                </div>
                <div className="rounded-full bg-white px-3 py-1 text-[11.5px] font-bold text-brand-700 sm:text-[12.5px]">
                  {progress}%
                </div>
              </div>
            </div>
            <div className="relative mt-3 h-1.5 overflow-hidden rounded-full bg-white/20 sm:mt-5">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.5, ease: "easeOut" }}
                className="h-full rounded-full bg-white"
              />
            </div>
          </motion.section>
        )}

        {/* Stepper */}
        <div className="mt-3 -mx-1 flex items-center gap-1.5 overflow-x-auto pb-1.5 sm:mt-5 sm:gap-2 sm:pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {steps.map((s, i) => {
            const Icon = s.Icon;
            const active = i === currentStep;
            const done = i < currentStep;
            return (
              <button
                key={s.id}
                type="button"
                ref={active ? activeStepRef : undefined}
                onClick={() => setCurrentStep(i)}
                className={`flex shrink-0 items-center gap-1.5 rounded-2xl border px-3 py-1.5 text-[12px] font-semibold transition sm:gap-2 sm:px-3.5 sm:py-2 sm:text-[12.5px] ${
                  active
                    ? "border-brand-600 bg-brand-gradient text-white shadow-soft"
                    : done
                    ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                    : "border-sand-300 bg-white text-ink-700 hover:border-brand-600 hover:text-brand-600"
                }`}
                title={s.title}
              >
                {done ? <Check size={12} /> : <Icon size={12} />}
                {s.title}
              </button>
            );
          })}
        </div>

        {/* Form card */}
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="card-elevated mt-3 p-4 sm:mt-5 sm:p-8"
        >
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-brand-50 text-brand-600 sm:h-12 sm:w-12">
              <StepIcon size={20} />
            </div>
            <div>
              <p className="section-subtle text-[10.5px]">Step {currentStep + 1}</p>
              <h2 className="font-display text-lg font-bold text-ink-800 sm:text-2xl">
                {steps[currentStep]?.title}
              </h2>
            </div>
          </div>

          <div className="mt-5 sm:mt-6">{renderStep()}</div>

          <AnimatePresence>
            {error && (
              <motion.p
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="mt-5 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-[13px] font-semibold text-rose-700"
              >
                {error}
              </motion.p>
            )}
            {notice && (
              <motion.p
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="mt-5 rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-[13px] font-semibold text-emerald-700"
              >
                {notice}
              </motion.p>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Auto-save hint (create flow only) */}
        {!isEditing && (
          <p className="mt-3 text-center text-[11.5px] text-ink-400">
            <ShieldCheck size={11} className="mr-1 inline-block text-emerald-500" />
            Auto-saving your progress on this device. Come back anytime.
          </p>
        )}

        <div className="h-6" />
      </main>

      {/* Sticky bottom action bar */}
      <div
        className="fixed inset-x-0 bottom-0 z-40 border-t border-sand-200 bg-white/95 backdrop-blur-xl shadow-[0_-4px_16px_-4px_rgba(40,18,9,0.1)]"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <div className="page-container flex items-center justify-between gap-2 py-2.5 sm:py-3">
          <button
            type="button"
            onClick={handlePrev}
            disabled={currentStep === 0 || saving}
            className="btn-ghost h-11 px-3 text-[13px] disabled:opacity-50 sm:h-12 sm:px-4 sm:text-[14px]"
          >
            <ChevronLeft size={15} /> Back
          </button>

          <div className="flex items-center gap-2 sm:gap-3">
            {isEditing ? (
              <>
                <button
                  type="button"
                  onClick={() => {
                    if (!validate()) return;
                    setCurrentStep((s) => Math.min(s + 1, steps.length - 1));
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                  disabled={currentStep === steps.length - 1}
                  className="btn-ghost h-11 px-3 text-[13px] disabled:opacity-50 sm:h-12 sm:px-4 sm:text-[14px]"
                >
                  Next <ChevronRight size={15} />
                </button>
                <button
                  type="button"
                  onClick={() => { if (!validate()) return; handleSaveChanges(); }}
                  disabled={saving}
                  className="btn-primary h-11 px-4 text-[13px] disabled:opacity-60 sm:h-12 sm:px-5 sm:text-[14px]"
                >
                  {saving ? <Loader2 className="animate-spin" size={15} /> : <Save size={15} />}
                  Save
                </button>
              </>
            ) : currentStep === steps.length - 1 ? (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={saving}
                className="btn-primary h-11 px-4 text-[13px] disabled:opacity-60 sm:h-12 sm:px-5 sm:text-[14px]"
              >
                {saving && <Loader2 className="animate-spin" size={15} />}
                Submit
              </button>
            ) : (
              <button
                type="button"
                onClick={handleNext}
                disabled={saving}
                className="btn-primary h-11 px-4 text-[13px] disabled:opacity-60 sm:h-12 sm:px-5 sm:text-[14px]"
              >
                Continue <ChevronRight size={15} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Unsaved Changes Modal */}
      <AnimatePresence>
        {showUnsavedModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowUnsavedModal(false)}
            className="fixed inset-0 z-[80] flex items-end justify-center bg-ink-900/50 px-4 backdrop-blur-sm sm:items-center"
          >
            <motion.div
              initial={{ y: 32, opacity: 0, scale: 0.97 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 24, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md overflow-hidden rounded-t-[28px] bg-white shadow-elevated sm:rounded-3xl"
            >
              <div className="flex flex-col items-center px-6 pt-8 pb-2 text-center">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-50 text-amber-600">
                  <ShieldCheck size={24} />
                </div>
                <h3 className="mt-4 font-display text-xl font-bold text-ink-800">
                  Unsaved changes
                </h3>
                <p className="mt-2 text-[13.5px] leading-6 text-ink-500">
                  You have unsaved changes. What would you like to do?
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3 px-6 py-5">
                <button
                  type="button"
                  onClick={() => {
                    setShowUnsavedModal(false);
                    navigate(-1);
                  }}
                  className="btn-ghost py-3"
                >
                  Discard
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowUnsavedModal(false);
                    handleSaveChanges();
                  }}
                  className="btn-primary py-3"
                >
                  Save changes
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Success Modal */}
      <AnimatePresence>
        {showSuccessModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowSuccessModal(false)}
            className="fixed inset-0 z-[80] flex items-center justify-center bg-ink-900/50 px-4 backdrop-blur-sm"
          >
            <motion.div
              initial={{ scale: 0.92, opacity: 0, y: 16 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-sm overflow-hidden rounded-3xl bg-white shadow-elevated"
            >
              <div className="flex flex-col items-center px-6 pt-8 pb-6 text-center">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50">
                  <Check size={28} className="text-emerald-600" />
                </div>
                <h3 className="mt-4 font-display text-xl font-bold text-ink-800">
                  Changes saved!
                </h3>
                <p className="mt-2 text-[13.5px] leading-6 text-ink-500">
                  Your profile has been updated successfully.
                </p>
              </div>
              <div className="border-t border-sand-200 px-6 py-4">
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="btn-primary w-full py-3"
                >
                  OK
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Registration progress modal */}
      <AnimatePresence>
        {submitState !== "idle" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[90] flex items-center justify-center bg-ink-900/60 px-4 backdrop-blur-sm"
          >
            <motion.div
              initial={{ scale: 0.94, opacity: 0, y: 16 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.96, opacity: 0 }}
              className="w-full max-w-sm overflow-hidden rounded-3xl bg-white shadow-elevated"
            >
              <div className="flex flex-col items-center px-6 pt-8 pb-7 text-center">
                <div className={`flex h-16 w-16 items-center justify-center rounded-full ${submitState === "success" ? "bg-emerald-50 text-emerald-600" : "bg-brand-50 text-brand-600"}`}>
                  {submitState === "success" ? (
                    <Check size={28} />
                  ) : (
                    <Loader2 size={28} className="animate-spin" />
                  )}
                </div>
                <h3 className="mt-4 font-display text-xl font-bold text-ink-800">
                  {submitState === "success" ? "Registration successful" : "Please wait"}
                </h3>
                <p className="mt-2 text-[13.5px] leading-6 text-ink-500">
                  {submitState === "success"
                    ? "Your profile has been created. Redirecting to your dashboard..."
                    : "Please wait, registering your profile..."}
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* useMemoCountries — small helper to memoize the static country list */
export default CreateProfile;
